import UpcastWriter from '@ckeditor/ckeditor5-engine/src/view/upcastwriter';
import signaturePlaceholder from './image-base64';
import { makeAnnotationElementStructure, makeAnnotationText } from './annotation-element-structure';
import {
  NO_FONT_FAMILY_INPUTS,
  NO_FONT_SIZE_CONTROL_INPUTS,
} from '../../../../src/config/agreement-editor';
import { ANNOTATION_FONT_SIZE_DEFAULT } from '../../../../src/config/ckeditor';

const clipboardInputHandler = (evt, data, viewDocument, dependencies = {}) => {
  const {
    draggedInputId,
    currentSectionId,
    getItemById,
    addAgreementInput,
    updateDraggedInputId,
    showBuefyToast,
    inputFontSizeMap,
  } = dependencies;

  if (!draggedInputId) return;

  const matchingItem = getItemById(draggedInputId);
  if (!matchingItem) return;

  let agreementInput = null;

  // add new annotation and new input
  if (matchingItem.type === 'INPUTTILE') {
    if (matchingItem.inputtileeligibility === 'AGREEMENTFORMONLY') {
      showBuefyToast({ message: 'Input is only available for Signer form' });
      evt.stop();
      return;
    }

    // add new input
    agreementInput = addAgreementInput({
      agreementOptions: {
        itemsource: matchingItem,
        itemfieldtype: matchingItem.inputtiletype,
        applyDateConfig: true,
      },
      sectionId: currentSectionId,
    });

    // add new annotation for existing input
  } else {
    if (matchingItem.itemdisplayeligiblity === 'AGREEMENTFORMONLY') {
      showBuefyToast({ message: 'Input is only available for Signer form' });
      evt.stop();
      return;
    }
    agreementInput = matchingItem;
  }

  // reset
  updateDraggedInputId(null);

  // Translate the annotation data to a view fragment.
  const writer = new UpcastWriter(viewDocument);
  const fragment = addAnnotation(writer, agreementInput, { inputFontSizeMap });
  if (!fragment) return;

  // Provide the content to the clipboard pipeline for further processing.
  data.content = fragment;
};

// add annotation to ckeditor
const addAnnotation = (writer, input, dependencies = {}) => {
  if (!input) return null;

  const { inputFontSizeMap = {} } = dependencies;

  // Translate the annotation data to a view fragment.
  const fragment = writer.createDocumentFragment();

  // add annotation
  const itemId = input.id;
  const itemFieldType = input.itemfieldtype;
  const itemColor = input.itemcolor;

  const fontFamily = input.fontfamily || 'Arial';
  const fontSize = inputFontSizeMap[itemId] || ANNOTATION_FONT_SIZE_DEFAULT;
  if (!inputFontSizeMap[itemId]) inputFontSizeMap[itemId] = fontSize;

  const generateAnnotationStyle = ({ fontFamily, fontSize, color, itemFieldType }) => {
    const hasFontFamily = !NO_FONT_FAMILY_INPUTS.includes(itemFieldType);
    const fontFamilyStyle = hasFontFamily ? `font-family: ${fontFamily};` : '';
    const hasFontSize = !NO_FONT_SIZE_CONTROL_INPUTS.includes(itemFieldType);
    const fontSizeStyle = hasFontSize ? `font-size: ${fontSize}px;` : '';
    const style = `background-color:${color}33; ${fontFamilyStyle} ${fontSizeStyle}`.trim();
    return style;
  };

  const styleString = generateAnnotationStyle({
    fontFamily,
    fontSize,
    color: itemColor,
    itemFieldType,
  });

  const annotationText = makeAnnotationText(input);

  const options = {
    itemId,
    width: `${annotationText.length + 1}em`,
    itemFieldType,
    style: styleString,
  };
  const { outer, inner } = makeAnnotationElementStructure(options);

  writer.appendChild(
    writer.createElement(outer.element, outer.attributes, [
      writer.createElement(inner.element, inner.attributes, annotationText),
    ]),
    fragment,
  );

  return fragment;
};

// insert placeholder image (e.g. for signature annotations)
const insertPlaceholderImage = (editor, fieldType, itemId) => {
  const fieldTypePlaceholders = {
    'signature-group': signaturePlaceholder,
  };

  const image = fieldTypePlaceholders[fieldType] || null;
  if (!image) {
    console.log(`Couldn't insert image for ${fieldType}`);
    return;
  }

  editor.execute('insertImage', {
    source: [
      {
        src: image,
        itemId,
        alt: itemId,
      },
    ],
  });
};

export { insertPlaceholderImage };
export default clipboardInputHandler;
